import type {
  LaunchRequest,
  LaunchResponse,
  OpenRoundRequest,
  OpenRoundResponse,
  ResolveRoundRequest,
  ResolveRoundResponse,
  RollbackRequest,
  RollbackResponse,
  BalanceResponse,
  RoundSeedsResponse,
} from "@vb/rgs-contract";

const BASE = (import.meta as unknown as { env: { VITE_RGS_BASE?: string } }).env.VITE_RGS_BASE ?? "";

// Module-local auth token. Set externally by GameView whenever the store's
// session token changes; attached as `Authorization: Bearer <jwt>` on every
// authed round-route request. Keeps lib/rgs-client free of a store import
// (avoiding a circular dep) without forcing every call site to thread the
// token through manually.
let currentToken: string | null = null;
export function setRgsAuthToken(token: string | null): void {
  currentToken = token;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status} ${res.statusText}: ${body}`);
  }
  return res.json() as Promise<T>;
}

async function post<TReq, TRes>(path: string, body: TReq, authed: boolean): Promise<TRes> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (authed && currentToken) headers.authorization = `Bearer ${currentToken}`;
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  return json<TRes>(res);
}

async function get<TRes>(path: string, authed: boolean): Promise<TRes> {
  const headers: Record<string, string> = {};
  if (authed && currentToken) headers.authorization = `Bearer ${currentToken}`;
  const res = await fetch(`${BASE}${path}`, { headers });
  return json<TRes>(res);
}

export const rgs = {
  // Public — operator gate runs server-side via X-API-Key (when applicable).
  launch: (body: LaunchRequest) => post<LaunchRequest, LaunchResponse>("/rgs/session/launch", body, false),
  // Round routes — require the launch JWT.
  open: (body: OpenRoundRequest) => post<OpenRoundRequest, OpenRoundResponse>("/rgs/round/open", body, true),
  resolve: (body: ResolveRoundRequest) =>
    post<ResolveRoundRequest, ResolveRoundResponse>("/rgs/round/resolve", body, true),
  rollback: (body: RollbackRequest) =>
    post<RollbackRequest, RollbackResponse>("/rgs/round/rollback", body, true),
  balance: (sessionId: string) =>
    get<BalanceResponse>(`/rgs/balance?sessionId=${encodeURIComponent(sessionId)}`, true),
  // Public — anyone can verify a round.
  seeds: (roundId: string) =>
    get<RoundSeedsResponse>(`/rgs/round/${encodeURIComponent(roundId)}/seeds`, false),
};
